import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { ASSETS, CATEGORIES } from "@/data/site";
import { useAuth } from "@/context/AuthContext";

const EMPTY_PROJECT = {
  name: "",
  location: "",
  category: "Residential",
  type: "",
  materials: "",
  services: "",
  image: "",
  description: "",
  featured: false,
};

function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-2xl bg-charcoal-card p-10 ring-1 ring-gold/25"
      >
        <img src={ASSETS.logo} alt="ShilpStones logo" className="mx-auto h-16 w-16 rounded-full object-cover ring-1 ring-gold/50" />
        <h1 className="mt-6 text-center font-serif text-3xl text-stone-100">Studio Login</h1>
        <p className="mt-2 text-center text-xs uppercase tracking-[0.25em] text-stone-500">
          ShilpStones Creations Admin
        </p>
        <label className="mt-8 block text-[11px] uppercase tracking-[0.2em] text-stone-400">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="mt-2 w-full rounded-lg border border-charcoal-border bg-obsidian px-4 py-3 text-sm text-stone-100 outline-none transition-colors focus:border-gold"
          placeholder="admin@shilpstones.com"
        />
        <label className="mt-5 block text-[11px] uppercase tracking-[0.2em] text-stone-400">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="mt-2 w-full rounded-lg border border-charcoal-border bg-obsidian px-4 py-3 text-sm text-stone-100 outline-none transition-colors focus:border-gold"
          placeholder="••••••••"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-lg bg-red-950/50 px-4 py-2.5 text-sm text-red-300">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-button"
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-obsidian transition-all hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign In
        </button>
      </form>
    </div>
  );
}

function ProjectForm({ initial, onSave, onClose, busy }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fields = [
    ["name", "Project Name"], ["location", "Location"], ["type", "Project Type"],
    ["materials", "Materials / Craft"], ["services", "Services Provided"], ["image", "Image URL"],
  ];
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/90 p-4 backdrop-blur-sm" data-testid="project-form-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-charcoal-card p-8 ring-1 ring-gold/25">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-stone-100">{initial.id ? "Edit Project" : "Add Project"}</h2>
          <button onClick={onClose} data-testid="project-form-close" className="text-stone-400 hover:text-gold">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); onSave(form); }}
          className="mt-6 grid gap-5 sm:grid-cols-2"
        >
          {fields.map(([key, label]) => (
            <div key={key} className={key === "image" ? "sm:col-span-2" : ""}>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</label>
              <input
                value={form[key]}
                required={key === "name" || key === "image"}
                onChange={(e) => set(key, e.target.value)}
                data-testid={`project-form-${key}`}
                className="mt-1.5 w-full rounded-lg border border-charcoal-border bg-obsidian px-4 py-2.5 text-sm text-stone-100 outline-none focus:border-gold"
              />
            </div>
          ))}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-stone-500">Category</label>
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              data-testid="project-form-category"
              className="mt-1.5 w-full rounded-lg border border-charcoal-border bg-obsidian px-4 py-2.5 text-sm text-stone-100 outline-none focus:border-gold"
            >
              {CATEGORIES.filter((c) => c !== "All").map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex cursor-pointer items-center gap-3 text-sm text-stone-300">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => set("featured", e.target.checked)}
                data-testid="project-form-featured"
                className="h-4 w-4 accent-[#D4AF37]"
              />
              Featured on homepage
            </label>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-stone-500">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              data-testid="project-form-description"
              className="mt-1.5 w-full rounded-lg border border-charcoal-border bg-obsidian px-4 py-2.5 text-sm text-stone-100 outline-none focus:border-gold"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="project-form-save"
            className="flex items-center justify-center gap-2 rounded-full bg-gold py-3 text-xs font-semibold uppercase tracking-[0.2em] text-obsidian transition-all hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] disabled:opacity-60 sm:col-span-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {initial.id ? "Save Changes" : "Create Project"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const loadProjects = useCallback(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const loadEnquiries = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadProjects();
    loadEnquiries();
  }, [loadProjects, loadEnquiries]);

  const save = async (form) => {
    setBusy(true);
    setNotice("");
    try {
      if (form.id) {
        await api.put(`/admin/projects/${form.id}`, form);
        setNotice("Project updated.");
      } else {
        await api.post("/admin/projects", form);
        setNotice("Project created.");
      }
      setEditing(null);
      loadProjects();
    } catch (err) {
      setNotice(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/projects/${p.id}`).catch(() => {});
    loadProjects();
  };

  return (
    <div className="mx-auto max-w-7xl px-6 pb-24 sm:px-12 lg:px-20" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow-light">Studio Dashboard</p>
          <h1 className="mt-2 font-serif text-3xl text-stone-100 sm:text-4xl">Welcome, {user.name}</h1>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="flex items-center gap-2 rounded-full border border-charcoal-border px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-stone-300 transition-colors hover:border-gold hover:text-gold"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>

      <div className="mt-8 flex gap-3" data-testid="admin-tabs">
        {["projects", "enquiries"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t}`}
            className={`rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] transition-all ${
              tab === t ? "bg-gold text-obsidian" : "border border-charcoal-border text-stone-400 hover:text-gold"
            }`}
          >
            {t === "projects" ? `Projects (${projects.length})` : `Enquiries (${enquiries.length})`}
          </button>
        ))}
      </div>

      {notice && (
        <p data-testid="admin-notice" className="mt-6 rounded-lg border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold-soft">
          {notice}
        </p>
      )}

      {tab === "projects" ? (
        <div className="mt-8">
          <button
            onClick={() => setEditing({ ...EMPTY_PROJECT })}
            data-testid="admin-add-project"
            className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian transition-all hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
          >
            <Plus className="h-4 w-4" />
            Add Project
          </button>
          <div className="mt-6 space-y-4">
            {projects.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-project-row-${p.id}`}
                className="flex flex-wrap items-center gap-5 rounded-xl bg-charcoal-card p-4 ring-1 ring-charcoal-border"
              >
                <img src={p.image} alt={p.name} className="h-16 w-24 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-serif text-lg text-stone-100">{p.name}</p>
                    {p.featured && <Star className="h-4 w-4 fill-gold text-gold" />}
                  </div>
                  <p className="text-xs uppercase tracking-[0.15em] text-stone-500">
                    {p.category} — {p.location}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditing({ ...p })}
                    data-testid={`admin-edit-${p.id}`}
                    className="rounded-full border border-charcoal-border p-2.5 text-stone-300 transition-colors hover:border-gold hover:text-gold"
                    aria-label="Edit project"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`admin-delete-${p.id}`}
                    className="rounded-full border border-charcoal-border p-2.5 text-stone-300 transition-colors hover:border-red-400 hover:text-red-400"
                    aria-label="Delete project"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="admin-enquiries-list">
          {enquiries.length === 0 && (
            <p className="rounded-xl bg-charcoal-card p-8 text-center text-sm text-stone-400 ring-1 ring-charcoal-border">
              No enquiries yet — they appear here whenever a visitor taps a WhatsApp button on the site.
            </p>
          )}
          {enquiries.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-charcoal-card px-5 py-4 ring-1 ring-charcoal-border">
              <div>
                <p className="text-sm text-stone-200">{e.context}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-stone-500">Page: {e.page}</p>
              </div>
              <p className="text-xs text-stone-500">{new Date(e.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProjectForm initial={editing} onSave={save} onClose={() => setEditing(null)} busy={busy} />
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  return (
    <div data-testid="admin-page" className="noise-overlay min-h-screen bg-obsidian pb-10 pt-28">
      {user === null ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gold" />
        </div>
      ) : user ? (
        <Dashboard />
      ) : (
        <LoginForm />
      )}
    </div>
  );
}
